//src/hooks/useNotifications.ts
import { useCallback, useEffect, useState } from "react";

import {
  listNotifications,
  markAllNotificationsAsRead,
  markNotificationAsRead,
} from "@/services/notifications";
import type { NotificationItem, NotificationPage } from "@/types/notifications";

export function useNotifications(params?: {
  page?: number;
  per_page?: number;
  is_read?: boolean;
  autoLoad?: boolean;
}) {
  const [data, setData] = useState<NotificationPage>({
    items: [],
    total: 0,
    page: params?.page ?? 1,
    per_page: params?.per_page ?? 10,
    total_pages: 1,
    unread_count: 0,
  });
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(Boolean(params?.autoLoad ?? true));
  const [actionLoading, setActionLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const pageData = await listNotifications({
        page: params?.page ?? 1,
        per_page: params?.per_page ?? 10,
        is_read: params?.is_read,
      });
      setData(pageData);
      setUnreadCount(pageData.unread_count);
    } finally {
      setLoading(false);
    }
  }, [params?.is_read, params?.page, params?.per_page]);

  useEffect(() => {
    if (params?.autoLoad === false) return;
    void load();
  }, [load, params?.autoLoad]);

  const markOneAsRead = useCallback(async (notificationId: string) => {
    setActionLoading(true);
    try {
      await markNotificationAsRead(notificationId);
      setData((current) => ({
        ...current,
        items: current.items.map((item) =>
          item.id === notificationId
            ? { ...item, is_read: true, read_at: new Date().toISOString() }
            : item,
        ),
        unread_count: Math.max(0, current.unread_count - 1),
      }));
      setUnreadCount((current) => Math.max(0, current - 1));
    } finally {
      setActionLoading(false);
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    setActionLoading(true);
    try {
      await markAllNotificationsAsRead();
      setData((current) => ({
        ...current,
        items: current.items.map((item) => ({
          ...item,
          is_read: true,
          read_at: item.read_at || new Date().toISOString(),
        })),
        unread_count: 0,
      }));
      setUnreadCount(0);
    } finally {
      setActionLoading(false);
    }
  }, []);

  const upsertPreviewItems = useCallback((items: NotificationItem[], unread: number) => {
    setData((current) => ({
      ...current,
      items,
      total: current.total,
    }));
    setUnreadCount(unread);
  }, []);

  return {
    data,
    unreadCount,
    loading,
    actionLoading,
    load,
    markOneAsRead,
    markAllAsRead,
    upsertPreviewItems,
  };
}
